// AgentBottomNav — Phase 2 fork of the player BottomNav.
//
// Same 3D popped bubble + REAL cutout architecture as the player
// version (single useMotionValue driving translate + mask-image
// gradient stop, bouncy spring, masked top border with arc), but
// adapted for the agent role and — critically — the active state
// uses the per-tab SECTION COLOR rather than the universal brand.
//
// Phase 3.5 (2026-05-11): "Money" was retired when the manual
// transactions / approval queues collapsed into the Players hub
// (and later the Agents hub). It's replaced by a "My Profile" tab
// — same 5-tab geometry, same 3D popped bubble + real cutout.
//
// Phase 8 (2026-05-11): "More" was retired. After Phases 3.5–7
// stripped its content down to a single useful destination (Rewards),
// it became a one-click middleman. The 4th tab now goes directly
// to /rewards, which gets a YELLOW section colour. The 3D bubble +
// real-cutout architecture is untouched — only the section key, the
// target URL, and the colour ramp moved.
//
//   '/'                Home       → indigo (Phase 8.1)
//   '/people/players'  Players    → sky
//   '/people/agents'   Agents     → emerald
//   '/rewards'         Rewards    → yellow
//   '/profile'         My Profile → brand magenta (Phase 8.1)
//
// Phase 8.1 (2026-05-11): Home and Profile section colours were
// SWAPPED. Home now wears the indigo that the dashboard's "My pool
// balance" hero card already uses, so the bottom-nav bubble matches
// the hero on the page it leads to. Profile picks up the brand
// magenta vacated by Home. Both new gradients use saturated stops
// across all three colour positions — no pale terminus — because
// the previous magenta dark-mode bubble was lightening through
// brand-300, which against arena-800 read as slightly transparent.
//
// Each section's gradient + label colour + bubble drop-shadow rgba
// are driven by SECTION_COLORS below. The bubble's class set is
// recomputed when activeIx changes; transitions stay buttery because
// the motion-value plumbing is untouched.
//
// Hidden on lg+ screens — desktop uses the AgentNavBar top pills.
// Admin role short-circuits and renders nothing (admin uses its own
// AdminBottomNavV2).

import { useEffect, useRef, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'motion/react';
import { useAdminAuth } from '../lib/adminAuth';
import { adminApi } from '../lib/adminApi';
import { useAgentWallet } from '../lib/agentWallet';
import { DEMO_BADGES, DEMO_VALUES } from '../lib/__demoBadges';
import { useT } from '../../lib/i18n';
import Icon from '../../components/Icon';

type SectionKey = 'home' | 'games' | 'players' | 'agents' | 'profile';

type Tab = {
  to: string;
  /** i18n key — resolved at render via useT(). Replaces the
   *  Phase B Group 1 hardcoded `label: string` (2026-05-12). */
  labelKey: string;
  icon: string;
  matchPrefix?: string;
  section: SectionKey;
  permissionKey?: string;
  /** Hide if the agent's own level exceeds this (leaf Agents get no sub-agent tab). */
  maxLevel?: number;
};

// 3-level hierarchy display names (mirrors shared/src/core/lib/levelNames.js).
const LEVEL_NAMES: Record<number, string> = { 1: 'Super Master', 2: 'Master', 3: 'Agent' };

// U2 (2026-07-09) — 6 tabs → 5 (owner directive):
//   * Cricket's slot becomes GAMES (/games) — cricket is the main game but
//     not the only one; the hub shows every game's earnings and cricket's
//     detail lives at /games/cricket.
//   * The Rewards tab is GONE — offers were an empty destination most of
//     the time; the machinery folds into Profile (U4). Old /rewards URLs
//     still resolve via router redirects.
const TABS: Tab[] = [
  { to: '/',                labelKey: 'agent.bottomnav.home',    icon: 'dashboard',      section: 'home' },
  { to: '/games',           labelKey: 'agent.bottomnav.games',   icon: 'receipt_long',   matchPrefix: '/games',          section: 'games' },
  { to: '/people/players',  labelKey: 'agent.bottomnav.players', icon: 'group',          matchPrefix: '/people/players', section: 'players', permissionKey: 'users' },
  { to: '/people/agents',   labelKey: 'agent.bottomnav.agents',  icon: 'support_agent',  matchPrefix: '/people/agents',  section: 'agents',  permissionKey: 'create_subagent', maxLevel: 2 },
  // UX Phase 6 (2026-08-08): "Profile" → "Money" (person icon → wallet), and it
  // opens straight on the wallet spoke so a broker's own money sits one tap
  // behind a MONEY word, not hidden under a person word. Section stays 'profile'
  // (keeps the magenta identity + the wallet-negative / deposit-zero danger dot).
  { to: '/profile/wallet',  labelKey: 'agent.bottomnav.money',   icon: 'account_balance_wallet', matchPrefix: '/profile', section: 'profile' },
];

// Each section's visual identity for the popped bubble. `rgba` is the
// raw RGB triplet used inside the drop-shadow utility.
const SECTION_COLORS: Record<SectionKey, {
  gradLight: string;
  gradDark: string;
  labelLight: string;
  labelDark: string;
  rgba: string;       // light-mode shadow rgb
  rgbaDark: string;   // dark-mode shadow rgb (often the same hue)
}> = {
  // U2 (2026-07-09): the Games hub wears VIOLET — a fifth distinct hue so
  // every tab keeps its own identity (Home indigo, Games violet, Players
  // sky, downline emerald, Profile magenta).
  games: {
    gradLight: 'from-violet-400 via-violet-500 to-violet-600',
    gradDark:  'from-violet-500 via-violet-500 to-violet-600',
    labelLight: 'text-violet-700',
    labelDark:  'text-violet-300',
    rgba:     '139,92,246',
    rgbaDark: '167,139,250',
  },
  // Phase 8.1: Home wears INDIGO to match the "My pool balance" hero
  // on the dashboard the tab opens. Dark-mode gradient stays
  // saturated (500 → 500 → 600) — no pale terminus, no apparent
  // transparency against arena-800.
  home: {
    gradLight: 'from-indigo-400 via-indigo-500 to-indigo-600',
    gradDark:  'from-indigo-500 via-indigo-500 to-indigo-600',
    labelLight: 'text-indigo-700',
    labelDark:  'text-indigo-300',
    rgba:     '99,102,241',
    rgbaDark: '129,140,248',
  },
  players: {
    gradLight: 'from-sky-400 via-sky-500 to-sky-600',
    gradDark:  'from-sky-500 via-sky-400 to-sky-300',
    labelLight: 'text-sky-700',
    labelDark:  'text-sky-300',
    rgba:     '14,165,233',
    rgbaDark: '56,189,248',
  },
  agents: {
    gradLight: 'from-emerald-400 via-emerald-500 to-emerald-600',
    gradDark:  'from-emerald-500 via-emerald-400 to-emerald-300',
    labelLight: 'text-emerald-700',
    labelDark:  'text-emerald-300',
    rgba:     '16,185,129',
    rgbaDark: '52,211,153',
  },
  // Phase 8.1: Profile takes the brand-magenta identity that Home
  // vacated. Same solid-dark-gradient pattern as Home (no pale
  // terminus) so the bubble reads as a solid magenta pill against
  // arena-800 instead of fading through brand-300.
  profile: {
    gradLight: 'from-brand-400 via-brand-500 to-brand-600',
    gradDark:  'from-brand-500 via-brand-500 to-brand-600',
    labelLight: 'text-brand-700',
    labelDark:  'text-brand-300',
    rgba:     'var(--brand-rgb)',
    rgbaDark: 'var(--brand-rgb)',
  },
};

const NAV_HEIGHT_PX = 52;
const BUBBLE_DIAMETER_PX = 42;
const NOTCH_RADIUS_PX = 32;
const BOUNCY_SPRING = { type: 'spring' as const, stiffness: 450, damping: 14, mass: 1 };

// Phase R7 follow-up (2026-05-14): pending-request badges on the
// Players and Agents tabs. The agent sees pending counts at-a-glance
// from anywhere in the app, not just while sitting on the requests
// page. Poll cadence matches PlayersHub / AgentsHub (30 s) — the
// hubs do their own poll for their on-page badge, this one is
// independent so nothing in the hubs has to change. The endpoints
// are the same `state=pending&limit=1` total-only queries already
// used in-page, so server load stays minimal.
// 2026-05-20 — 429-remediation. The 3 separate poll effects below
// were consolidated into ONE coordinated hook. Each fetcher has its
// own cooldown; the unified hook ticks every 30 s and runs whichever
// fetchers are due. Reasons:
//   1. `loc.pathname` is OUT of the dep array — navigating between
//      pages no longer triggers an immediate burst of 6 API calls.
//      Previous behaviour: every route change re-ran 3 effects, each
//      doing `void fetchXxx()` BEFORE re-arming setInterval → 5 fast
//      clicks = 30 immediate fetches. Now: navigations cost ZERO API
//      requests.
//   2. Intervals were bumped (30s→60s for counts, 60s→120s for the
//      other two) per ops directive. Approximately halves the
//      steady-state load.
//   3. `document.hidden` is honoured — backgrounded tabs don't poll
//      at all. Matches AgentDashboard's existing behaviour.
//   4. Single setInterval instead of three — one cleanup, one
//      teardown, no churn when deps update.
const PENDING_POLL_MS = 60_000;  // was 30_000
const PLAYER_PENDING_ENDPOINTS = [
  '/admin/payment-requests/recharge?state=pending&limit=1&offset=0',
  '/admin/payment-requests/withdrawal?state=pending&limit=1&offset=0',
] as const;
const AGENT_PENDING_ENDPOINTS = [
  '/admin/me-agent/payment-requests/agent-recharge?state=pending&limit=1&offset=0',
  '/admin/me-agent/payment-requests/agent-withdrawal?state=pending&limit=1&offset=0',
] as const;

// Phase R7 follow-up (2026-05-14): rewards badge = count of platform
// offers the agent hasn't opted into yet. There's no dedicated count
// endpoint — the agent app fetches the full offer list and the panel
// filters client-side (BrowseOffersPanel does the same). Offers change
// less often than payment requests, so we poll at half cadence (60 s).
const REWARDS_POLL_MS = 120_000;  // was 60_000 (429-remediation)
const REWARDS_ENDPOINT = '/admin/me-agent/offers';

// Phase R7 follow-up (2026-05-14): home tab badge = count of direct
// sub-agents whose margin_status is in {warning, breach, suspended}.
// This is the same set surfaced by the on-page DownlineDefaulterBanner,
// so a sub-agent in trouble shows up as ONE badge on the home tab and
// ONE banner row on the home page — the visual chain is unbroken. We
// piggyback on the existing agent-home-summary endpoint that the
// dashboard already polls; range=today keeps the response light.
const HOME_POLL_MS = 120_000;  // was 60_000 (429-remediation)
const HOME_ENDPOINT = '/admin/reports/agent-home-summary?range=today';
type HomeSummary = {
  downline: { children: Array<{ margin_status: 'safe'|'caution'|'warning'|'breach'|'suspended' }> };
};

export default function AgentBottomNav() {
  const { admin, canDo } = useAdminAuth();
  const t = useT();
  const loc = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const [activeX, setActiveX] = useState(-200);
  const x = useMotionValue(activeX);

  // Filter tabs by permission for the current agent.
  const tabs = useMemo(
    () => TABS.filter((t) =>
      (!t.permissionKey || canDo(t.permissionKey, 'view')) &&
      (t.maxLevel == null || admin?.level == null || admin.level <= t.maxLevel)),
    [canDo, admin]
  );
  // The downline tab is named for what THIS agent creates (Super Master → Masters,
  // Master → Agents; an Agent has it hidden entirely).
  const childLabelPlural = `${LEVEL_NAMES[Number(admin?.level ?? 1) + 1] || 'Agent'}s`;
  const labelFor = (it: Tab) => (it.to === '/people/agents' ? childLabelPlural : t(it.labelKey));

  // Phase R7 follow-up (2026-05-14): pending counts for the two
  // request-style tabs. Independent of the hubs' own poll — the
  // bottom nav has to stay live even when the agent is on Home,
  // Wallet, Rewards, etc., so polling lives here. We only poll
  // endpoints whose tab the agent can actually see (some agents may
  // lack `users` or `create_subagent` permission); the corresponding
  // pending count stays at 0 for them.
  const showPlayersTab = useMemo(() => tabs.some((t) => t.section === 'players'), [tabs]);
  const showAgentsTab  = useMemo(() => tabs.some((t) => t.section === 'agents'),  [tabs]);
  const [playerPending, setPlayerPending] = useState(0);
  const [agentPending,  setAgentPending]  = useState(0);

  // Phase R7 follow-up (2026-05-14): count of platform offers the
  // agent hasn't opted into yet → drives the Rewards-tab badge.
  // Filtered client-side from the same list BrowseOffersPanel
  // already fetches; the response is small (single-digit row count
  // in practice) so re-fetching every 60 s is comfortable.
  const [rewardsPending, setRewardsPending] = useState(0);

  // Phase R7 follow-up (2026-05-14): downline-defaulter count for
  // the Home tab. Same data as the on-page DownlineDefaulterBanner
  // so the agent can connect "badge on Home" → "banner on page".
  const [homeDefaulters, setHomeDefaulters] = useState(0);

  // Phase R7 follow-up (2026-05-14): wallet + deposit signals for
  // the two emoji indicators on the Profile tab.
  //   walletNegative → 😨 scared face (top-right)
  //   depositZero    → 😳 shamed face (top-left)
  // Wallet comes from the global store (already polled app-wide).
  // Deposit comes from the admin auth /me payload — refreshed on
  // every /me re-fetch, which happens after key wallet/deposit
  // mutations. DEMO_BADGES forces both ON for visual review.
  const { balance: walletBalance } = useAgentWallet();
  const liveWalletNegative = walletBalance < 0;
  const liveDepositBalance = Number((admin as any)?.depositBalance ?? 0);
  const liveDepositZero    = liveDepositBalance === 0;
  const walletNegative = DEMO_BADGES ? true : liveWalletNegative;
  const depositZero    = DEMO_BADGES ? true : liveDepositZero;

  // 2026-05-20 — Single coordinated badge-poll hook (was 3 separate
  // effects). Deps are NOW `[admin, showPlayersTab, showAgentsTab]`
  // — `loc.pathname` removed so navigating between pages NEVER
  // triggers immediate badge fetches. The hook:
  //
  //   • runs each fetcher ONCE on first mount (initial badge state)
  //   • ticks every 30 s on a single setInterval
  //   • each tick checks per-fetcher cooldowns and only runs the ones
  //     whose interval has elapsed:
  //         counts  : every 60 s   (PENDING_POLL_MS)
  //         rewards : every 120 s  (REWARDS_POLL_MS)
  //         home    : every 120 s  (HOME_POLL_MS)
  //   • skips entirely when `document.hidden` — matches the dashboard's
  //     existing tab-hidden guard
  //
  // Net effect at steady state for one agent:
  //   ~1 req/min (counts × 4 endpoints split across 60s, plus rewards
  //   + home each at 120 s) instead of the previous ~10 req/min +
  //   burst-on-navigation.
  useEffect(() => {
    if (!admin || admin.role !== 'agent') return;
    let alive = true;

    const fetchOne = (url: string) => adminApi
      .get<{ total: number }>(url)
      .then((r) => r.total || 0)
      .catch(() => 0);

    const fetchCounts = async () => {
      const [pr, pw, ar, aw] = await Promise.all([
        showPlayersTab ? fetchOne(PLAYER_PENDING_ENDPOINTS[0]) : Promise.resolve(0),
        showPlayersTab ? fetchOne(PLAYER_PENDING_ENDPOINTS[1]) : Promise.resolve(0),
        showAgentsTab  ? fetchOne(AGENT_PENDING_ENDPOINTS[0])  : Promise.resolve(0),
        showAgentsTab  ? fetchOne(AGENT_PENDING_ENDPOINTS[1])  : Promise.resolve(0),
      ]);
      if (!alive) return;
      setPlayerPending(pr + pw);
      setAgentPending(ar + aw);
    };

    const fetchRewards = async () => {
      try {
        const r = await adminApi.get<{ offers: Array<{ am_opted_in: 0 | 1 | boolean }> }>(
          REWARDS_ENDPOINT
        );
        if (!alive) return;
        const n = (r.offers || []).filter((o) => !o.am_opted_in).length;
        setRewardsPending(n);
      } catch { /* keep last good value */ }
    };

    const fetchHome = async () => {
      try {
        const r = await adminApi.get<HomeSummary>(HOME_ENDPOINT);
        if (!alive) return;
        const n = (r.downline?.children || []).filter(
          (c) => c.margin_status === 'warning'
              || c.margin_status === 'breach'
              || c.margin_status === 'suspended'
        ).length;
        setHomeDefaulters(n);
      } catch { /* keep last good value */ }
    };

    // Per-fetcher last-run timestamps; used to gate the unified tick
    // so each fetcher honours its own cadence within the shared clock.
    let countsLastAt  = 0;
    let rewardsLastAt = 0;
    let homeLastAt    = 0;

    const tick = () => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const now = Date.now();
      if (now - countsLastAt  >= PENDING_POLL_MS) { countsLastAt  = now; void fetchCounts();  }
      if (now - rewardsLastAt >= REWARDS_POLL_MS) { rewardsLastAt = now; void fetchRewards(); }
      if (now - homeLastAt    >= HOME_POLL_MS)    { homeLastAt    = now; void fetchHome();    }
    };

    // First-mount immediate run for all three (no perceived lag on
    // login). After that, the shared 30s tick gates by cooldowns.
    tick();
    const iv = setInterval(tick, 30_000);
    return () => { alive = false; clearInterval(iv); };
  }, [admin, showPlayersTab, showAgentsTab]);

  const activeIx = tabs.findIndex((it) =>
    it.matchPrefix
      ? loc.pathname === it.to || loc.pathname.startsWith(it.matchPrefix + '/') || loc.pathname === it.matchPrefix
      : it.to === '/'
        ? loc.pathname === '/'
        : loc.pathname.startsWith(it.to)
  );
  const activeTab = activeIx >= 0 ? tabs[activeIx] : null;
  const activeColors = activeTab ? SECTION_COLORS[activeTab.section] : SECTION_COLORS.home;

  // Measure the active LI's centre x in nav-local coordinates.
  useEffect(() => {
    if (!admin || admin.role !== 'agent' || !ulRef.current || !navRef.current) return;
    const measure = () => {
      const li = ulRef.current!.children[activeIx] as HTMLElement | undefined;
      if (!li) { setActiveX(-200); return; }
      const liRect = li.getBoundingClientRect();
      const navRect = navRef.current!.getBoundingClientRect();
      setActiveX(liRect.left - navRect.left + liRect.width / 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(navRef.current);
    ro.observe(ulRef.current);
    return () => ro.disconnect();
  }, [activeIx, admin]);

  // Drive the motion value with the bouncy spring on activeX change.
  useEffect(() => {
    const controls = animate(x, activeX, BOUNCY_SPRING);
    return () => controls.stop();
  }, [activeX, x]);

  // Cutout mask-image derived from the motion value.
  const maskImage = useTransform(
    x,
    (cx) => `radial-gradient(circle ${NOTCH_RADIUS_PX}px at ${cx}px 0px, transparent ${NOTCH_RADIUS_PX - 1}px, black ${NOTCH_RADIUS_PX}px)`
  );

  // Only agents see this nav; admin SPA uses the AdminBottomNavV2 fork.
  if (!admin || admin.role !== 'agent') return null;

  return (
    <nav
      ref={navRef}
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden
                 pb-[env(safe-area-inset-bottom)]"
    >
      {/* Masked nav strip — page content shows through the cutout. */}
      <motion.div
        aria-hidden="true"
        style={{ maskImage, WebkitMaskImage: maskImage }}
        className="absolute inset-0
                   bg-white dark:bg-arena-800
                   backdrop-blur
                   border-t border-arena-200/60 dark:border-arena-600/60"
      />

      {/* Curved arc under the cutout — driven by the same motion value. */}
      <motion.div
        aria-hidden="true"
        style={{
          x,
          marginLeft: -NOTCH_RADIUS_PX,
          width: NOTCH_RADIUS_PX * 2,
          height: NOTCH_RADIUS_PX,
          top: 0,
          borderBottomLeftRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
          borderBottomRightRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
        }}
        className="absolute left-0 z-20 pointer-events-none
                   border-b border-arena-200/60 dark:border-arena-600/60"
      />

      {/* Tab list — active tab's icon is hidden; bubble overlay shows it. */}
      <ul
        ref={ulRef}
        className="relative z-30 max-w-xl mx-auto flex justify-around items-end"
        style={{ height: NAV_HEIGHT_PX }}
      >
        {tabs.map((it, ix) => {
          const isActive = ix === activeIx;
          const colors = SECTION_COLORS[it.section];
          // Phase 4 follow-up — the Profile tab swaps the generic
          // 'person' icon for the agent's actual avatar image, matching
          // the player-end bottom-nav pattern. Falls back to the icon
          // when no avatar is set (new agents pre-upload).
          const isAvatarTab = it.section === 'profile' && !!admin.avatar_url;
          // U2 (2026-07-09): per-tab badge wiring after the 6→5 restructure.
          //   home     → downline defaulter count (magenta count)
          //   players  → pending payment requests (magenta count)
          //   agents   → pending sub-agent requests (magenta count)
          //   profile  → un-opted-in offers count (the rewards surface
          //              lives inside Profile now), UNLESS the agent's own
          //              money needs attention — then a single rose DANGER
          //              DOT replaces the count (emoji indicators retired:
          //              a pulsing dot is legible at any size and doesn't
          //              fight the luxury look).
          // DEMO_BADGES forces every count to its DEMO_VALUES preset
          // so the user can see every badge fire in one screenshot.
          const liveCount =
              it.section === 'home'    ? homeDefaulters
            : it.section === 'players' ? playerPending
            : it.section === 'agents'  ? agentPending
            : it.section === 'profile' ? rewardsPending
            : 0;
          const demoCount =
              it.section === 'home'    ? DEMO_VALUES.homeDefaulters
            : it.section === 'players' ? DEMO_VALUES.playerPending
            : it.section === 'agents'  ? DEMO_VALUES.agentPending
            : it.section === 'profile' ? DEMO_VALUES.rewardsPending
            : 0;
          const profileDanger = it.section === 'profile' && (walletNegative || depositZero);
          const badgeCount = profileDanger ? 0 : (DEMO_BADGES ? demoCount : liveCount);
          return (
            <li key={it.to} className="relative flex-1 h-full">
              <NavLink
                to={it.to}
                end={it.to === '/'}
                className="relative flex flex-col items-center justify-end h-full outline-none"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Flat icon — visible when this tab is NOT active.
                    The badge lives INSIDE this wrapper so it tucks
                    into the icon's top-right and inherits the same
                    visibility lifecycle: when this tab is active the
                    bubble overlay takes over and the badge naturally
                    hides (the agent is already on the page, so the
                    count is redundant there). The wrapper is already
                    `absolute` which creates an implicit positioning
                    context for the badge — no extra `relative` class
                    needed. */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-[10px]
                             flex items-center justify-center pointer-events-none"
                  style={{ visibility: isActive ? 'hidden' : 'visible' }}
                >
                  {isAvatarTab ? (
                    <img
                      src={admin.avatar_url!}
                      alt=""
                      className="w-[22px] h-[22px] rounded-full object-cover
                                 ring-1 ring-arena-300/70 dark:ring-arena-600/70"
                    />
                  ) : (
                    <Icon
                      name={it.icon}
                      size={22}
                      filled
                      weight={400}
                      className="text-arena-500 dark:text-neon-200/60"
                    />
                  )}
                  {/* Pending-count badge — same magenta as the
                      in-page tab badges and the /profile/wallet hero
                      (#e91e8c, tailwind `brand-500`). AnimatePresence
                      gives a clean scale-in when the first request
                      arrives and a scale-out when the agent clears
                      the queue. `layout` smooths the width change
                      between "9" and "10". The badge is purely
                      decorative — pointer-events-none from the
                      parent wrapper carries through, so taps still
                      hit the NavLink underneath. */}
                  <AnimatePresence>
                    {badgeCount > 0 && (
                      <motion.span
                        key="pending-badge"
                        layout
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 520, damping: 26, mass: 0.8 }}
                        className="absolute -top-1.5 -right-2.5
                                   inline-flex items-center justify-center
                                   h-[17px] min-w-[17px] px-1
                                   rounded-full
                                   text-[9.5px] font-extrabold tabular-nums
                                   bg-brand-500 text-white
                                   shadow-[0_1px_4px_rgba(233,30,140,0.55)]
                                   ring-[1.5px] ring-white dark:ring-arena-800"
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* U2 (2026-07-09): Profile-tab DANGER DOT — replaces the
                      retired 😨/😳 emoji pair. One pulsing rose dot means
                      "your own money needs attention" (negative wallet OR
                      empty deposit); the Profile page itself explains which.
                      A dot stays legible at any pixel size, reads as urgent
                      in both themes, and keeps the luxury look. */}
                  <AnimatePresence>
                    {profileDanger && (
                      <motion.span
                        key="profile-danger-dot"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 520, damping: 26, mass: 0.8 }}
                        aria-label={walletNegative ? 'Wallet balance is negative' : 'Security deposit is zero'}
                        className="absolute -top-1 -right-1.5 w-[11px] h-[11px] rounded-full
                                   bg-rose-500
                                   ring-[1.5px] ring-white dark:ring-arena-800
                                   shadow-[0_0_8px_rgba(244,63,94,0.7)]"
                      >
                        <span aria-hidden className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-60" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                {/* Label — section colour on active, muted otherwise. */}
                <span
                  className={`absolute bottom-1 left-1/2 -translate-x-1/2
                              text-[11px] leading-none transition-all
                              ${isActive
                                ? `font-extrabold tracking-wide ${colors.labelLight} dark:${colors.labelDark}`
                                : 'font-medium text-arena-500 dark:text-neon-200/60'}`}
                >
                  {labelFor(it)}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* Bubble overlay — single motion.div, follows the same x. The
          per-section gradient + drop-shadow rgba flip whenever the
          active tab changes so the bubble is indigo on Home, sky on
          Players, emerald on Agents, yellow on Rewards, brand magenta
          on Profile. Phase 8.1 swapped Home↔Profile so the Home
          bubble matches the page's indigo Pool Balance hero. */}
      {activeTab && (
        <motion.div
          style={{
            x,
            marginLeft: -(BUBBLE_DIAMETER_PX / 2),
            top: -(BUBBLE_DIAMETER_PX / 2),
            width: BUBBLE_DIAMETER_PX,
            height: BUBBLE_DIAMETER_PX,
            // Per-section glow — uses arbitrary value so it can read
            // the `rgba` triplet from SECTION_COLORS at render time.
            filter: `drop-shadow(0 4px 12px rgba(${activeColors.rgba}, 0.45))`,
          }}
          animate={{ scale: 1.15 }}
          transition={BOUNCY_SPRING}
          className={`absolute left-0 z-50 rounded-full
                      flex items-center justify-center
                      bg-gradient-to-br ${activeColors.gradLight}
                      dark:bg-gradient-to-br dark:${activeColors.gradDark}
                      ring-2 ring-white/80 dark:ring-arena-900/80
                      pointer-events-none`}
        >
          {activeTab.section === 'profile' && admin.avatar_url ? (
            <img
              src={admin.avatar_url}
              alt=""
              className="w-[34px] h-[34px] rounded-full object-cover
                         ring-2 ring-white/80 dark:ring-arena-900/80"
            />
          ) : (
            <Icon
              name={activeTab.icon}
              size={28}
              filled
              weight={600}
              className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
            />
          )}
        </motion.div>
      )}
    </nav>
  );
}
